import { resolveIdentity, UNRESOLVED_IDENTITY } from './identity';
import { applyKeyAliases } from './key-aliases';

export function resolveRow(map, row) {
  const entry = map[row.action];
  if (!entry) {
    return fallbackResult({ debugData: row.debugData, reason: 'noMapEntry' });
  }
  return resolveRowAgainstEntry(entry, row);
}

// Exported so the formatter, which is handed an entry rather than a map, runs this same dispatch
// instead of keeping a second copy of the downgrade rule.
export function resolveRowAgainstEntry(entry, { debugData }) {
  const result = matchedRowResult({ entry, debugData });
  if (entry.identity === 'record' && !result.identity.resolved) {
    return fallbackResult({
      debugData,
      entry,
      reason: 'identityUnresolved',
      attribution: downgradedAttribution(entry, debugData, result.identity),
    });
  }
  return result;
}

function matchedRowResult({ entry, debugData }) {
  const identity = resolveIdentity(entry, debugData);
  const { normalized, collisions } = applyKeyAliases(entry, debugData);

  return {
    entry,
    entity: entry.entity,
    identity,
    normalizedDebugData: normalized,
    aliasCollisions: collisions,
    ...resolveAttribution(entry, debugData, identity),
    renderMode: entry.renderMode,
    fallbackReason: null,
  };
}

// A resolved identity can still be unattributable: a join-row id identifies a row nobody can
// navigate to, so we know WHICH join row changed but not which child record it names. That is a
// property of the alias that matched, which is why it is declared on the alias and checked here
// rather than inferred from the payload — the same key is present in both writer generations.
function resolveAttribution(entry, debugData, identity) {
  if (identity?.resolved && matchedAliasIsNonNavigable(entry, identity.matchedAlias)) {
    return { unattributed: true, unattributedReason: 'identityNotNavigable' };
  }

  const nameOnlyMatch = (entry.parentNameKeys ?? []).find(
    ref => debugData?.[ref.key] != null && !resolvedById(entry, debugData, identity, ref.entity)
  );
  return nameOnlyMatch
    ? { unattributed: true, unattributedReason: 'parentMatchedByName' }
    : { unattributed: false, unattributedReason: null };
}

// A row is unattributed when a name is its ONLY handle to that entity. A name key sitting alongside
// an id for the same entity is a display convenience, not the handle — flagging on mere presence
// would mark every healthy row whose id resolves perfectly well. The id arrives two ways: a
// parentRefs key for another entity, or, for a self-referential name key, the row's own resolved
// identity. A legacy shape declares such a name key precisely because it carries no id, so the
// presence of an id is what separates the generations.
function resolvedById(entry, debugData, identity, namedEntity) {
  if (namedEntity === entry.entity && identity?.resolved) return true;
  return (entry.parentRefs ?? []).some(ref => ref.entity === namedEntity && debugData?.[ref.key] != null);
}

function matchedAliasIsNonNavigable(entry, matchedAlias) {
  return (entry.idKeys ?? []).some(
    alias => typeof alias === 'object' && alias.key === matchedAlias && alias.unattributed === true
  );
}

// Without an identity nobody can say which record the row is about, so a downgraded row is
// unattributed even when no name matches it. A name-only match is the more specific reason.
function downgradedAttribution(entry, debugData, identity) {
  const { unattributedReason } = resolveAttribution(entry, debugData, identity);
  return { unattributed: true, unattributedReason: unattributedReason ?? 'identityUnresolved' };
}

// Only 'noMapEntry' keeps the false default: with no entry, the row was never expected to name a
// record.
function fallbackResult({
  debugData,
  entry = null,
  reason,
  attribution = { unattributed: false, unattributedReason: null },
}) {
  return {
    entry,
    entity: null,
    identity: UNRESOLVED_IDENTITY,
    normalizedDebugData: debugData ?? {},
    aliasCollisions: [],
    ...attribution,
    renderMode: 'keyValue',
    fallbackReason: reason,
  };
}
